import { useContext, useEffect, useState } from "react";
import { UserContext } from "../../components/UserContext";
import { BookingData } from "../../utils/bookingUtils";
import axios from "axios";
import Cookies from "js-cookie";
import ContactCustomerModal from "../../components/ContactCustomerModal";
import RejectBookingModal from "../../components/RejectBookingModal";
import { toast } from "react-toastify";
import { BookingStatus, BookingType } from "../../constants-generated/well-known-values";

interface BookingResponse {
  success: boolean;
  message: string;
  bookings: BookingData[];
}

interface WorkspaceBookingCardProps extends BookingData {
  onConfirmBooking: (bookingId: number) => void;
  onRejectBooking: (booking: BookingData) => void;
  onContactCustomer: (booking: BookingData) => void;
}

function WorkspaceBookingCard(booking: WorkspaceBookingCardProps) {
  const [isConfirming, setIsConfirming] = useState(false);

  const getStatusColor = (status: string): string => {
    switch (status) {
      case BookingStatus.Confirmed:
        return "bg-green-100 text-green-800";
      case BookingStatus.Pending:
        return "bg-yellow-100 text-yellow-800";
      case BookingStatus.Cancelled:
        return "bg-red-100 text-red-800";
      case BookingStatus.Completed:
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatDateRange = (startDate: string, endDate: string): string => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "short",
      day: "numeric",
    };
    return `${start.toLocaleDateString("en-US", options)} - ${end.toLocaleDateString("en-US", options)}`;
  };

  const isReservation = booking.bookingType === BookingType.Reservation;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow p-6">
      {/* Header */}
      <div className="flex justify-between items-start mb-4 gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-gray-900 mb-1 truncate">
            {booking.workspaceTitle}
          </h3>
          <p className="text-sm text-gray-600 truncate">
            {booking.workspaceAddress}, {booking.workspaceCity}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
          <span
            className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(booking.status)}`}
          >
            {booking.status}
          </span>
          <span
            className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
              isReservation
                ? "bg-blue-100 text-blue-800"
                : "bg-purple-100 text-purple-800"
            }`}
          >
            {isReservation ? "Reservation" : "Tour"}
          </span>
        </div>
      </div>

      {/* Booking Details */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">
            Booking ID
          </label>
          <p className="text-sm font-medium text-gray-900">#{booking.id}</p>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">
            Customer ID
          </label>
          <p className="text-sm font-medium text-gray-900">#{booking.userId}</p>
        </div>
      </div>

      {/* Booking Period */}
      <div className="bg-gray-50 rounded-lg p-3 mb-4">
        <div className="flex items-center gap-2 text-sm text-gray-700 mb-2">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
              clipRule="evenodd"
            />
          </svg>
          <span className="font-medium">Booking Period:</span>
        </div>
        <p className="text-sm text-gray-900 font-medium">
          {formatDateRange(booking.startDate, booking.endDate)}
        </p>
      </div>

      {/* Revenue and Payment Status */}
      <div className="flex justify-between items-center mb-4">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">
            Revenue
          </label>
          <span className="text-lg font-bold text-green-600">
            ${booking.totalPrice.toLocaleString()}
          </span>
        </div>
        {booking.paymentStatus && (
          <div className="text-right">
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Payment
            </label>
            <span className="bg-gray-100 text-gray-700 text-xs font-medium px-2 py-1 rounded">
              {booking.paymentStatus}
            </span>
          </div>
        )}
      </div>

      {/* Customer Message */}
      {booking.message && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
          <div className="flex items-start gap-2">
            <svg
              className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z"
                clipRule="evenodd"
              />
            </svg>
            <div>
              <p className="text-xs font-medium text-blue-800 mb-1">
                Customer Message:
              </p>
              <p className="text-blue-800 text-sm">{booking.message}</p>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="space-y-3">
        {booking.status === BookingStatus.Pending ? (
          <div className="flex gap-3">
            <button
              onClick={() => {
                setIsConfirming(true);
                booking.onConfirmBooking(booking.id);
              }}
              disabled={isConfirming}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white text-sm font-medium py-2 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isConfirming ? "Confirming..." : "Confirm Booking"}
            </button>
            <button
              onClick={() => booking.onRejectBooking(booking)}
              disabled={isConfirming}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white text-sm font-medium py-2 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Reject Booking
            </button>
          </div>
        ) : (
          <button
            disabled
            className="w-full bg-gray-300 text-gray-500 text-sm font-medium py-2 px-4 rounded-lg cursor-not-allowed"
          >
            {booking.status === BookingStatus.Confirmed
              ? "Already Confirmed"
              : booking.status}
          </button>
        )}

        <button
          onClick={() => booking.onContactCustomer(booking)}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2 px-4 rounded-lg transition-colors"
        >
          Contact Customer
        </button>
      </div>
    </div>
  );
}

export default function WorkspaceBookingsPage() {
  const { user } = useContext(UserContext);
  const [bookings, setBookings] = useState<BookingData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<BookingData | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState("All");
  const [workspaceFilter, setWorkspaceFilter] = useState("All");
  const [typeFilter, setTypeFilter] = useState<"All" | "Reservations" | "Tours">("All");

  const handleConfirmBooking = async (bookingId: number) => {
    try {
      const token = Cookies.get("authToken");
      const response = await axios.post(
        `/api/booking/confirm`,
        { bookingId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setBookings((prev) =>
          prev.map((b) =>
            b.id === bookingId ? { ...b, status: BookingStatus.Confirmed } : b
          )
        );
        toast.success("Booking confirmed! Customer has been notified via email.", {
          position: "top-right",
          autoClose: 5000,
        });
      } else {
        toast.error(response.data.message || "Failed to confirm booking", {
          position: "top-right",
          autoClose: 5000,
        });
      }
    } catch (err) {
      console.error("Error confirming booking:", err);
      toast.error("An error occurred while confirming the booking", {
        position: "top-right",
        autoClose: 5000,
      });
    }
  };

  const handleRejectBooking = (booking: BookingData) => {
    setSelectedBooking(booking);
    setIsRejectModalOpen(true);
  };

  const handleRejectBookingSubmit = async (
    bookingId: number,
    rejectionReason: string
  ) => {
    try {
      const token = Cookies.get("authToken");
      const response = await axios.post(
        `/api/booking/reject`,
        { bookingId, rejectionReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setBookings((prev) =>
          prev.map((b) =>
            b.id === bookingId ? { ...b, status: BookingStatus.Cancelled } : b
          )
        );
        toast.success(
          "Booking rejected successfully. Customer has been notified via email.",
          { position: "top-right", autoClose: 5000 }
        );
      } else {
        throw new Error(response.data.message || "Failed to reject booking");
      }
    } catch (err: unknown) {
      console.error("Error rejecting booking:", err);
      if (axios.isAxiosError(err)) {
        throw new Error(
          err.response?.data?.message || err.message || "Failed to reject booking"
        );
      }
      throw new Error("Failed to reject booking");
    }
  };

  const handleContactCustomer = (booking: BookingData) => {
    setSelectedBooking(booking);
    setIsContactModalOpen(true);
  };

  const handleSendMessage = async (message: string) => {
    if (!selectedBooking) return;
    try {
      const token = Cookies.get("authToken");
      const response = await axios.post(
        `/api/booking/contact-customer`,
        { bookingId: selectedBooking.id, message },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.data.success) {
        throw new Error(response.data.message || "Failed to send message");
      }
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        throw new Error(
          err.response?.data?.message || err.message || "Failed to send message"
        );
      }
      throw new Error("Failed to send message");
    }
  };

  useEffect(() => {
    const fetchWorkspaceBookings = async () => {
      if (!user) return;
      try {
        const token = Cookies.get("authToken");
        const response = await axios.get<BookingResponse>(
          `/api/booking/list-workspace-owner?OwnerId=${user.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.success) {
          setBookings(response.data.bookings);
          setError(null);
        } else {
          setError(response.data.message || "Failed to fetch workspace bookings");
        }
      } catch (err) {
        console.error("Error fetching workspace bookings:", err);
        setError("An error occurred while fetching workspace bookings");
      } finally {
        setLoading(false);
      }
    };

    fetchWorkspaceBookings();
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
              <p className="text-gray-600">Loading workspace bookings...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center py-12">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
              <svg
                className="w-12 h-12 text-red-600 mx-auto mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <h3 className="text-lg font-medium text-red-900 mb-2">
                Error Loading Workspace Bookings
              </h3>
              <p className="text-red-700">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Derived stats ──────────────────────────────────────────────────────────
  const totalRevenue = bookings.reduce((s, b) => s + b.totalPrice, 0);
  const pendingCount = bookings.filter(
    (b) => b.status === BookingStatus.Pending
  ).length;
  const confirmedCount = bookings.filter(
    (b) => b.status === BookingStatus.Confirmed
  ).length;
  const toursCount = bookings.filter(
    (b) => b.bookingType === BookingType.Tour
  ).length;

  // ── Unique workspace titles for filter dropdown ────────────────────────────
  const uniqueWorkspaces = Array.from(
    new Set(bookings.map((b) => b.workspaceTitle))
  ).sort();

  // ── Filtered bookings ──────────────────────────────────────────────────────
  const filteredBookings = bookings.filter((b) => {
    const matchesStatus = statusFilter === "All" || b.status === statusFilter;
    const matchesWorkspace =
      workspaceFilter === "All" || b.workspaceTitle === workspaceFilter;
    const matchesType =
      typeFilter === "All" ||
      (typeFilter === "Reservations" && b.bookingType === BookingType.Reservation) ||
      (typeFilter === "Tours" && b.bookingType === BookingType.Tour);
    return matchesStatus && matchesWorkspace && matchesType;
  });

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Workspace Bookings
          </h1>
          <p className="text-gray-600">
            Manage bookings and reservations for your workspaces
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  Total Bookings
                </h3>
                <p className="text-gray-600 text-sm">All time</p>
              </div>
              <div className="bg-blue-100 text-blue-800 text-2xl font-bold px-4 py-2 rounded-lg">
                {bookings.length}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  Total Revenue
                </h3>
                <p className="text-gray-600 text-sm">From all bookings</p>
              </div>
              <div className="bg-green-100 text-green-800 text-xl font-bold px-3 py-2 rounded-lg">
                ${totalRevenue.toLocaleString()}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  Pending Approval
                </h3>
                <p className="text-gray-600 text-sm">Require attention</p>
              </div>
              <div className="bg-yellow-100 text-yellow-800 text-2xl font-bold px-4 py-2 rounded-lg">
                {pendingCount}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  Tours Scheduled
                </h3>
                <p className="text-gray-600 text-sm">
                  {confirmedCount} confirmed
                </p>
              </div>
              <div className="bg-purple-100 text-purple-800 text-2xl font-bold px-4 py-2 rounded-lg">
                {toursCount}
              </div>
            </div>
          </div>
        </div>

        {/* Bookings List */}
        {bookings.length > 0 ? (
          <div className="space-y-6">
            {/* Type toggle tab */}
            <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1 w-fit">
              {(["All", "Reservations", "Tours"] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setTypeFilter(tab)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    typeFilter === tab
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900">
                {filteredBookings.length} Booking
                {filteredBookings.length !== 1 ? "s" : ""}
              </h2>
              <div className="flex gap-2">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                >
                  <option value="All">All Statuses</option>
                  <option value={BookingStatus.Pending}>Pending</option>
                  <option value={BookingStatus.Confirmed}>Confirmed</option>
                  <option value={BookingStatus.Completed}>Completed</option>
                  <option value={BookingStatus.Cancelled}>Cancelled</option>
                </select>
                <select
                  value={workspaceFilter}
                  onChange={(e) => setWorkspaceFilter(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                >
                  <option value="All">All Workspaces</option>
                  {uniqueWorkspaces.map((title) => (
                    <option key={title} value={title}>
                      {title}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {filteredBookings.length > 0 ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {filteredBookings.map((booking) => (
                  <WorkspaceBookingCard
                    key={booking.id}
                    {...booking}
                    onConfirmBooking={handleConfirmBooking}
                    onRejectBooking={handleRejectBooking}
                    onContactCustomer={handleContactCustomer}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
                <p className="text-gray-500">
                  No bookings match the current filters.
                </p>
                <button
                  onClick={() => {
                    setStatusFilter("All");
                    setWorkspaceFilter("All");
                    setTypeFilter("All");
                  }}
                  className="mt-3 text-sm text-blue-600 hover:underline"
                >
                  Clear filters
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 max-w-lg mx-auto">
              <svg
                className="w-16 h-16 text-gray-400 mx-auto mb-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 5H7a2 2 0 00-2 2v6a2 2 0 002 2h6a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                />
              </svg>
              <h3 className="text-xl font-medium text-gray-900 mb-2">
                No Workspace Bookings
              </h3>
              <p className="text-gray-600 mb-6">
                You don't have any bookings for your workspaces yet. Once
                customers start booking, they'll appear here.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <ContactCustomerModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        customerName={
          selectedBooking ? `User #${selectedBooking.userId}` : "Customer"
        }
        bookingId={selectedBooking?.id || 0}
        onSendMessage={handleSendMessage}
      />

      <RejectBookingModal
        isOpen={isRejectModalOpen}
        onClose={() => setIsRejectModalOpen(false)}
        bookingId={selectedBooking?.id || 0}
        customerName={
          selectedBooking ? `User #${selectedBooking.userId}` : "Customer"
        }
        onRejectBooking={handleRejectBookingSubmit}
      />
    </div>
  );
}
